// frontend/src/services/deliveryService.js
import api from '../utils/axiosConfig';

class DeliveryService {
  /**
   * Calculate delivery charges using address coordinates
   * @param {number} latitude - User's address latitude
   * @param {number} longitude - User's address longitude
   * @param {string} accessToken - User's auth token
   * @returns {Promise<Object>} Delivery information
   */
  async getDeliveryInfoFromCoordinates(latitude, longitude, accessToken = null) {
    try {
      console.log('🚀 [DeliveryService] Calculating delivery for coordinates:', { 
        latitude, 
        longitude,
        hasToken: !!accessToken
      });

      // ✅ FIX: Ensure token is set on window for axios interceptor
      if (accessToken && !window.accessToken) {
        window.accessToken = accessToken;
      }

      const response = await api.post('/delivery/calculate', { 
        latitude, 
        longitude 
      });

      console.log('✅ [DeliveryService] Delivery response:', response.data);

      if (response.data.success) {
        return {
          distance: parseFloat(response.data.distance),
          distanceText: response.data.distanceText || `${response.data.distance} km`,
          deliveryCharge: response.data.deliveryCharge,
          isFreeDelivery: response.data.isFreeDelivery,
          estimatedDeliveryTime: response.data.estimatedDeliveryTime,
          message: response.data.message,
          duration: response.data.duration,
          usedOSRM: response.data.usedOSRM,
        };
      }

      throw new Error(response.data.message || 'Failed to calculate delivery');
    } catch (error) {
      console.error('❌ [DeliveryService] Delivery calculation error:', error);
      console.error('Error details:', {
        response: error.response?.data,
        status: error.response?.status,
        message: error.message
      });
      
      // Return user-friendly error message
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Unable to calculate delivery charges';
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Calculate delivery charges using address ID
   * @param {string} addressId - MongoDB address ID
   * @param {string} accessToken - User's auth token
   * @returns {Promise<Object>} Delivery information
   */
  async getDeliveryInfoFromAddressId(addressId, accessToken = null) {
    try {
      console.log('🚀 [DeliveryService] Calculating delivery for address ID:', addressId);

      // ✅ FIX: Ensure token is set on window for axios interceptor
      if (accessToken && !window.accessToken) {
        window.accessToken = accessToken;
      }

      const response = await api.post('/delivery/calculate', { 
        addressId 
      });

      console.log('✅ [DeliveryService] Delivery response:', response.data);

      if (response.data.success) {
        return {
          distance: parseFloat(response.data.distance),
          distanceText: response.data.distanceText || `${response.data.distance} km`,
          deliveryCharge: response.data.deliveryCharge,
          isFreeDelivery: response.data.isFreeDelivery,
          estimatedDeliveryTime: response.data.estimatedDeliveryTime,
          message: response.data.message,
          duration: response.data.duration,
          usedOSRM: response.data.usedOSRM,
        };
      }

      throw new Error(response.data.message || 'Failed to calculate delivery');
    } catch (error) {
      console.error('❌ [DeliveryService] Delivery calculation error:', error);
      
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Unable to calculate delivery charges';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Wrapper method for backward compatibility with your AddressPage
   * This matches the exact method name used in your AddressPage.jsx line 96
   * @param {number} latitude 
   * @param {number} longitude 
   * @param {string} accessToken 
   * @returns {Promise<Object>}
   */
  async getDeliveryInfoFromAddress(latitude, longitude, accessToken = null) {
    return this.getDeliveryInfoFromCoordinates(latitude, longitude, accessToken);
  }
}

// Export singleton instance
export default new DeliveryService();